/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useMemo, useState } from 'react';
import type { ActionType } from '@rc-component/trigger/lib/interface';
import { Flex, Form, Popover, Row } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import IErrorInline from 'Assets/icons/warning-info.svg';
import IError from 'Assets/icons/error-circle.svg';

import styles from './styles.less';

import Text from '@/components/Text';

export type CustomWarningType = { type: 'error' | 'warning'; message: string } | undefined;
export type ErrorPosition = 'default' | 'bottom';

interface Props {
  children?: React.ReactNode;
  isError?: boolean;
  inline?: boolean;
  label?: string | React.ReactNode;
  errorMessage?: string;
  className?: string;
  triggerError?: ActionType | ActionType[];
  popoverPosition?: TooltipPlacement;
  hideTooltip?: boolean;
  disabled?: boolean;
  required?: boolean;
  customWarning?: CustomWarningType;
  errorPosition?: ErrorPosition;
}

const ImgIcon = ({
  src,
  onClick,
  className,
}: {
  src: string;
  onClick?: () => void;
  className?: string;
}) => {
  return (
    // prettier-ignore
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <img src={src} alt="Validate Error" className={className} onClick={() => onClick && onClick()} /> // nosonar
  );
};

const ErrorMessage = ({
  showError,
  setShowError,
  errorMessage,
  inline,
}: {
  setShowError: (isShowError: boolean) => void;
  inline?: boolean;
  errorMessage?: string;
  showError: boolean;
}) => {
  return (
    <Flex align="center" className={classNames('text-error', styles.errorWrapper)}>
      <ImgIcon
        src={IError}
        className={classNames(styles.imgIcon, { [styles.cursorPointer]: inline })}
        onClick={() => {
          setShowError(!showError);
        }}
      />
      <Text className="text-xs" type="danger">
        {!inline && errorMessage}
      </Text>
    </Flex>
  );
};

function FieldLayout({
  children,
  isError,
  inline,
  label,
  errorMessage,
  className,
  triggerError = 'click',
  popoverPosition = 'right',
  hideTooltip = false,
  disabled,
  customWarning,
  required,
  errorPosition = 'default',
}: Readonly<Props>) {
  const { validateOnChange } = useFormikContext();
  const [showError, setShowError] = useState(false);
  const [showCustomWarning, setShowCustomWarning] = useState(false);

  const isErr = useMemo(() => {
    const error = !disabled && isError;
    if (!validateOnChange) return !!error;
    return !!error;
  }, [disabled, isError, validateOnChange]);

  const fieldValidateStatus = useMemo(() => {
    return isErr ? 'error' : customWarning?.type ?? '';
  }, [isErr, customWarning?.type]);

  return (
    <Form.Item
      validateStatus={fieldValidateStatus}
      className={classNames(className, { [styles.inlineWrap]: !label })}
      style={{ marginBottom: 0 }}>
      {label && (
        <div
          className={classNames('flex justify-between form-item-label', {
            'mb-1': label,
          })}>
          <label>
            {label} {required && <span className="text-error">*</span>}
          </label>
          {isErr && errorPosition === 'default' && (
            <ErrorMessage
              errorMessage={errorMessage}
              setShowError={setShowError}
              inline={inline}
              showError={showError}
            />
          )}
        </div>
      )}
      <Popover
        placement={isErr ? popoverPosition : 'top'}
        open={
          !hideTooltip &&
          ((isErr && showError && (!label || inline)) || (!!customWarning && showCustomWarning))
        }
        onOpenChange={open => {
          if (isErr) {
            setShowError(open);
          } else if (customWarning) {
            setShowCustomWarning(open);
          }
        }}
        getPopupContainer={trigger => trigger.parentElement || document.body}
        content={
          <Flex align="center">
            {errorMessage && <ImgIcon src={IErrorInline} className={styles.inlineImgIcon} />}
            <div className={styles.messagePopover}>
              {errorMessage ??
                (customWarning?.type && (
                  <Text type={customWarning.type === 'error' ? 'danger' : 'warning'}>
                    {customWarning?.message}
                  </Text>
                ))}
            </div>
          </Flex>
        }
        trigger={customWarning ? 'hover' : triggerError}>
        <div>{children}</div>
      </Popover>
      {isErr && errorPosition === 'bottom' && (
        <Row className="mt-2">
          <ErrorMessage
            errorMessage={errorMessage}
            setShowError={setShowError}
            inline={inline}
            showError={showError}
          />
        </Row>
      )}
    </Form.Item>
  );
}

export default FieldLayout;
